import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../../hooks/useQuery";
import { UserContext } from "../../../../context/UserContext";
import {
  isStatMedicine,
  formatMedicineDaysUsed,
  formatMedicineDurationModalLabel,
} from "../../../../helpers/medicineKeys";
import { updateMedicationPrescribeAPI } from "../../../../services/patient.services";
import "../../InspectionSummaries/InspectionSummaries.css";
import { BellOutlined, CloseOutlined, CaretRightOutlined } from "@ant-design/icons";
import useIsNurseMobile from "./useIsNurseMobile";
import { LANDING_PAGE_SIZE } from "./useLandingVisibleItems";
import LandingShowMore from "./LandingShowMore";

const MEDICINE_SECTIONS = [
  { key: "non-antibiotic", title: "Non-antibiotic", countClass: "non-antibiotic" },
  { key: "antibiotic", title: "Antibiotic", countClass: "antibiotic" },
  { key: "stopped", title: "Stopped", countClass: "stopped" },
];

const MEDICINE_COUNT_KEY = {
  antibiotic: "antibiotic",
  "non-antibiotic": "nonAntibiotic",
  stopped: "stopped",
};

const SummaryMedicines = ({ data, refreshPage }) => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const isNurseMobile = useIsNurseMobile();
  const [noteDismissed, setNoteDismissed] = useState(false);
  const [sectionVisibleCount, setSectionVisibleCount] = useState({});
  const [editDurationRecord, setEditDurationRecord] = useState(null);
  const [editCanExtendDuration, setEditCanExtendDuration] = useState(false);
  const [editDurationSaving, setEditDurationSaving] = useState(false);
  const [editDurationForm] = Form.useForm();

  const role = userDetails?.currRole?.toLowerCase()?.trim();
  const isDoctor = role?.includes("doctor");

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const getMedTimestamp = (med) => {
    const raw =
      med?.created_at ?? med?.added_at ?? med?.prescribed_at ?? med?.updated_at;
    const t = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(t) ? t : 0;
  };

  const categorizedMeds = useMemo(() => {
    const src = Array.isArray(data) ? data : [];
    const sorted = [...src].sort((a, b) => getMedTimestamp(b) - getMedTimestamp(a));

    const stopped = [];
    const antibiotic = [];
    const nonAntibiotic = [];

    sorted.forEach((med) => {
      const status = String(med?.status ?? "").trim();
      if (status === "Stopped") {
        stopped.push(med);
        return;
      }
      if (med?.is_antibiotic === true) {
        antibiotic.push(med);
      } else {
        nonAntibiotic.push(med);
      }
    });

    return { antibiotic, nonAntibiotic, stopped };
  }, [data]);

  const medsEndingToday = useMemo(() => {
    return (data || []).filter((med) => {
      const duration = safeNum(med?.duration);
      const noOfDays = safeNum(med?.no_of_days);
      const status = String(med?.status ?? "").trim();
      return status === "Started" && duration > 0 && noOfDays >= duration;
    });
  }, [data]);

  const getDaysProgress = (record) => {
    const noOfDays = safeNum(record?.no_of_days ?? record?.days_left_adjusted);
    const duration = safeNum(record?.duration);
    return { noOfDays, duration };
  };

  const canExtendDuration = (record) => {
    if (isStatMedicine(record)) return false;
    const { noOfDays, duration } = getDaysProgress(record);
    return duration > 0 && noOfDays === duration;
  };

  const openEditDurationModal = (record) => {
    setEditDurationRecord(record);
    setEditCanExtendDuration(canExtendDuration(record));
    editDurationForm.setFieldsValue({
      extended_duration: undefined,
      comments: record?.comments ?? "",
    });
  };

  const closeEditDurationModal = () => {
    setEditDurationRecord(null);
    setEditCanExtendDuration(false);
    editDurationForm.resetFields();
  };

  const submitEditDuration = () => {
    editDurationForm.validateFields().then((values) => {
      const record = editDurationRecord;
      if (!record?.id) return;

      const rawExtend = values?.extended_duration;
      const hasExtendValue =
        editCanExtendDuration &&
        rawExtend !== undefined &&
        rawExtend !== null &&
        rawExtend !== "" &&
        safeNum(rawExtend) > 0;

      const nextComments = (values?.comments ?? "").trim();
      const prevComments = (record?.comments ?? "").trim();

      if (!hasExtendValue && nextComments === prevComments) {
        message.info("No changes to save");
        return;
      }

      const payload = {};
      if (hasExtendValue) {
        const extendBy = safeNum(rawExtend);
        const currentTotal = safeNum(
          record?.days_left_initial ?? record?.duration
        );
        payload.extended_duration = extendBy;
        payload.days_left_initial = currentTotal + extendBy;
      }
      if (nextComments !== prevComments) {
        payload.comments = nextComments;
      }

      setEditDurationSaving(true);
      updateMedicationPrescribeAPI(record.id, payload)
        .then((res) => {
          message.success(res?.data?.message);
          closeEditDurationModal();
          refreshPage?.();
        })
        .catch((err) => message.error(err?.response?.data?.message))
        .finally(() => setEditDurationSaving(false));
    });
  };

  const stopMedicine = (id) => {
    setEditDurationSaving(true);
    updateMedicationPrescribeAPI(id, { status: "Stopped" })
      .then((res) => {
        message.success(res?.data?.message);
        closeEditDurationModal();
        refreshPage?.();
      })
      .catch((err) => message.error(err?.response?.data?.message))
      .finally(() => setEditDurationSaving(false));
  };

  const editDaysProgress = getDaysProgress(editDurationRecord || {});

  const medicineSections = useMemo(
    () =>
      MEDICINE_SECTIONS.map((section) => {
        const medsKey = MEDICINE_COUNT_KEY[section.key];
        const meds = categorizedMeds[medsKey] || [];
        return { ...section, meds, count: meds.length };
      }).filter((section) => section.meds.length > 0),
    [categorizedMeds]
  );

  const defaultOpenSectionKeys = useMemo(() => {
    const keys = [];
    if (categorizedMeds.nonAntibiotic.length > 0) keys.push("non-antibiotic");
    if (categorizedMeds.antibiotic.length > 0) keys.push("antibiotic");
    return keys;
  }, [categorizedMeds]);

  const getVisibleCount = (sectionKey, total) => {
    const count = sectionVisibleCount[sectionKey] ?? LANDING_PAGE_SIZE;
    return Math.min(count, total);
  };

  const showMoreSection = (sectionKey, total) => {
    setSectionVisibleCount((prev) => ({
      ...prev,
      [sectionKey]: Math.min(
        (prev[sectionKey] ?? LANDING_PAGE_SIZE) + LANDING_PAGE_SIZE,
        total
      ),
    }));
  };

  const showLessSection = (sectionKey) => {
    setSectionVisibleCount((prev) => ({
      ...prev,
      [sectionKey]: LANDING_PAGE_SIZE,
    }));
  };

  const renderMedicineTable = (sectionKey, meds) => {
    const total = meds.length;
    const visibleCount = isNurseMobile
      ? getVisibleCount(sectionKey, total)
      : total;
    const visibleMeds = isNurseMobile ? meds.slice(0, visibleCount) : meds;
    const hasMore = isNurseMobile && visibleCount < total;
    const canShowLess = isNurseMobile && visibleCount > LANDING_PAGE_SIZE;

    return (
      <>
        <table className="summary-medicines-table">
          <thead>
            <tr>
              <th>Medicine Name</th>
              <th>Days</th>
            </tr>
          </thead>
          <tbody>
            {visibleMeds.map((med, index) => {
              const duration = safeNum(med?.duration);
              const noOfDays = safeNum(med?.no_of_days);
              const remainingDays = duration - noOfDays;

              return (
                <tr key={`${sectionKey}-${med?.id ?? med?.name ?? "med"}-${index}`}>
                  <td>
                    <span className="summary-medicines-table-name">
                      {med?.name}
                      {sectionKey === "stopped" && med?.is_antibiotic === true && (
                        <Tag
                          color="green"
                          style={{
                            borderRadius: 999,
                            fontSize: 12,
                            lineHeight: "20px",
                            paddingInline: 10,
                            marginInlineEnd: 0,
                          }}
                        >
                          Antibiotic
                        </Tag>
                      )}
                    </span>
                  </td>
                  <td>
                    <Tag
                      bordered={false}
                      className={
                        remainingDays < 3 ? "interpretation-tag-severe" : "score-tag"
                      }
                    >
                      {formatMedicineDaysUsed(noOfDays, duration)}
                    </Tag>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {isNurseMobile ? (
          <LandingShowMore
            hasMore={hasMore}
            canShowLess={canShowLess}
            onMore={() => showMoreSection(sectionKey, total)}
            onLess={() => showLessSection(sectionKey)}
          />
        ) : null}
      </>
    );
  };

  const handleNavigate = () => {
    if (role?.includes("nurse")) {
      navigate(
        `/nurse/home?status=${params?.status}&patient=${params?.patient}&type=medicine`
      );
    } else {
      navigate(
        `/doctor/home?patient-type=${params?.["patient-type"]}&status=${params?.status}&patient=${params?.patient}&type=medicine`
      );
    }
  };

  return (
    <>
      {isDoctor && !noteDismissed && medsEndingToday?.length > 0 && (
        <div
          className="landing-med-note"
          style={{
            border: "1px solid #F7DFB6",
            borderRadius: 12,
            padding: 16,
            background: "#FFFCF5",
            marginBottom: 10,
            boxShadow:
              "0px 2px 2px -1px var(--ColorsEffectsShadowsshadow-lg03), 0px 4px 6px -2px var(--ColorsEffectsShadowsshadow-lg02), 0px 12px 16px -4px var(--ColorsEffectsShadowsshadow-lg01)",
            display: "flex",
            gap: 12,
            alignItems: "stretch",
            position: "relative",
          }}
        >
          <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      background: "#FFFAEB",
                      border: "1px solid #F7DFB6",
                      display: "grid",
                      placeItems: "center",
                      boxShadow:
                        "0 0 0 4px rgba(247, 223, 182, 0.30), 0 0 0 8px rgba(247, 223, 182, 0.16)",
                      flex: "0 0 auto",
                    }}
                  >
                    <BellOutlined style={{ fontSize: 16, color: "#B54708" }} />
                  </div>

                  <Typography.Text
                    style={{ fontWeight: 600, color: "#101828" }}
                  >
                    NOTE
                  </Typography.Text>
                </div>

                <Button
                  type="text"
                  size="small"
                  className="landing-med-note-dismiss"
                  icon={<CloseOutlined />}
                  onClick={() => setNoteDismissed(true)}
                  aria-label="Dismiss note"
                />
              </div>
              <Typography.Text style={{ color: "#475467" }}>
                The prescribed duration for the following medications ends
                today:
              </Typography.Text>
            </div>

            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {medsEndingToday.map((m, idx) => (
                <Tag
                  key={`${m?.id ?? m?.name ?? "med"}-${idx}`}
                  onClick={() => openEditDurationModal(m)}
                  style={{
                    borderRadius: 999,
                    border: "1px solid #F7DFB6",
                    background: "#FFFAEB",
                    color: "#B54708",
                    marginInlineEnd: 0,
                    paddingInline: 10,
                    lineHeight: "22px",
                    cursor: "pointer",
                  }}
                >
                  {m?.name}
                </Tag>
              ))}
            </div>

            <Typography.Text
              style={{ display: "block", marginTop: 8, color: "#667085" }}
            >
              Tap a medication to review and take action.
            </Typography.Text>
          </div>
        </div>
      )}

      <Modal
        title={editCanExtendDuration ? "Edit duration & notes" : "Edit notes"}
        open={!!editDurationRecord}
        onCancel={closeEditDurationModal}
        footer={null}
        destroyOnClose
      >
        <Form form={editDurationForm} layout="vertical" requiredMark={false}>
          {editCanExtendDuration && (
            <Form.Item
              label={formatMedicineDurationModalLabel(
                editDaysProgress.noOfDays,
                editDaysProgress.duration
              )}
              name="extended_duration"
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    const n = Number(value);
                    if (!Number.isFinite(n) || n < 0) {
                      return Promise.reject("Enter a valid number of days");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                min={0}
                placeholder="Additional days"
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
          <Form.Item label="Notes" name="comments">
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Popconfirm
              title="Sure to stop"
              onConfirm={() => stopMedicine(editDurationRecord?.id)}
            >
              <Button danger loading={editDurationSaving}>
                Stop
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              loading={editDurationSaving}
              onClick={submitEditDuration}
            >
              Submit
            </Button>
          </Space>
        </Form>
      </Modal>

      <Card
        className={`summary-medicines-card landing-summary-card${
          isNurseMobile ? " landing-medicines-card" : ""
        }`}
        title={
          <Typography onClick={handleNavigate} style={{ cursor: "pointer" }}>
            Summary Medicines
          </Typography>
        }
      >
        <Collapse
          bordered={false}
          className={`summary-medicines-collapse${
            isNurseMobile ? " summary-medicines-collapse--icon-end" : ""
          }`}
          defaultActiveKey={defaultOpenSectionKeys}
          expandIconPosition={isNurseMobile ? "end" : "start"}
          expandIcon={({ isActive }) => (
            <span
              className={`summary-medicines-expand-icon${
                isActive ? " summary-medicines-expand-icon--active" : ""
              }`}
            >
              <CaretRightOutlined />
            </span>
          )}
          items={medicineSections.map((section) => ({
            key: section.key,
            label: (
              <div className="summary-medicines-collapse-header">
                <Typography.Text style={{ fontWeight: 700, color: "#101828" }}>
                  {section.title}
                </Typography.Text>
                <Tag
                  bordered
                  className={`summary-medicines-count-tag summary-medicines-count-tag-${section.countClass}`}
                >
                  {section.count}
                </Tag>
              </div>
            ),
            children: renderMedicineTable(section.key, section.meds),
          }))}
        />
      </Card>
    </>
  );
};

export default SummaryMedicines;
